import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type TableRowActionMenuProps = {
  onViewDetails?: () => void;
  onEdit?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onSyncList?: () => void;
  onArchive?: () => void;
  archiveLabel?: string;
  archiveTone?: "danger" | "success";
  onDelete?: () => void;
  editLabel?: string;
  deleteLabel?: string;
  deleteTone?: "danger" | "success";
  deleteDisabled?: boolean;
  className?: string;
  viewDetailsLabel?: string;
};

export default function TableRowActionMenu({
  onViewDetails,
  onEdit,
  onPause,
  onResume,
  onSyncList,
  onArchive,
  archiveLabel = "Archive",
  archiveTone = "success",
  onDelete,
  editLabel = "Edit",
  deleteLabel = "Delete",
  deleteTone = "danger",
  deleteDisabled = false,
  className = "",
  viewDetailsLabel = "View details",
}: TableRowActionMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 110,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const clickedOutsideTrigger = rootRef.current ? !rootRef.current.contains(target) : true;
      const clickedOutsideMenu = menuRef.current ? !menuRef.current.contains(target) : true;
      if (clickedOutsideTrigger && clickedOutsideMenu) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={rootRef}
      className={["relative inline-flex", className].filter(Boolean).join(" ")}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
       
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-dark-grey transition-colors hover:bg-background-grey hover:text-dark-black"
      >
        <img src="/icons/more.svg" alt="" aria-hidden="true" className="h-4 w-4" />
      </button>

      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              className="fixed z-50 min-w-[110px] overflow-hidden rounded-lg border border-light-grey bg-white shadow-sm"
              style={{ top: menuPosition.top, left: menuPosition.left }}
            >
              {onViewDetails ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onViewDetails();
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-dark-black transition-colors hover:bg-background-grey"
                >
                  {viewDetailsLabel}
                </button>
              ) : null}
              {onEdit ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onEdit();
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-dark-black transition-colors hover:bg-background-grey"
                >
                  {editLabel}
                </button>
              ) : null}
              {onPause ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onPause();
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-dark-black transition-colors hover:bg-background-grey"
                >
                  Pause
                </button>
              ) : null}
              {onResume ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onResume();
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-dark-black transition-colors hover:bg-background-grey"
                >
                  Resume
                </button>
              ) : null}
              {onSyncList ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onSyncList();
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-dark-black transition-colors hover:bg-background-grey"
                >
                  Sync lists
                </button>
              ) : null}
              {onArchive ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onArchive();
                  }}
                  className={[
                    "block w-full px-3 py-2 text-left text-sm transition-colors",
                    archiveTone === "success"
                      ? "text-success hover:bg-success-bg"
                      : "text-danger hover:bg-danger/10",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {archiveLabel}
                </button>
              ) : null}
              {onDelete ? (
                <button
                  type="button"
                  role="menuitem"
                  disabled={deleteDisabled}
                  onClick={() => {
                    if (deleteDisabled) return;
                    setOpen(false);
                    onDelete();
                  }}
                  className={[
                    "block w-full px-3 py-2 text-left text-sm transition-colors",
                    deleteDisabled ? "cursor-not-allowed opacity-50 hover:bg-transparent" : "",
                    deleteTone === "success"
                      ? "text-success hover:bg-success-bg"
                      : "text-danger hover:bg-danger/10",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {deleteLabel}
                </button>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
